import {
  cancelLlmKeyOpsPoll,
  fetchEnvironmentById,
  fetchLlmKeyById,
  pollLlmKeyOperations,
  selectEnvironmentById,
  selectLlmKeyById,
} from "@app/deploy";
import { findLoaderComposite } from "@app/loaders";
import { useQuery, useSelector } from "@app/react";
import {
  environmentLlmKeysUrl,
  llmKeyDetailUrl,
  llmKeyModelsUrl,
  llmKeyRequestsUrl,
} from "@app/routes";
import type { DeployLlmKey } from "@app/types";
import { usePoller } from "@app/ui/hooks";
import { useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import {
  CRUMB_VARIANTS,
  DetailPageHeaderView,
  Pill,
  ResourceDetailBar,
  ResourceDetailItem,
  ResourceDetailsTable,
  type TabItem,
} from "../shared";
import { Tooltip } from "../shared/tooltip";
import { AppSidebarLayout } from "./app-sidebar-layout";

export function LlmKeyHeader({
  llmKey,
  isLoading,
}: {
  llmKey: DeployLlmKey;
  isLoading: boolean;
}) {
  const {
    currentUsage: {
      totalRequests,
      successfulRequests,
      failedRequests,
      spend,
      inputTokens,
      outputTokens,
      totalTokens,
    },
  } = llmKey;

  return (
    <div className="flex flex-col gap-4">
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          <img
            src="/resource-types/logo-llm_key.png"
            className="w-[32px] h-[32px]"
            aria-label="LLM Key"
          />
        }
        nameLabel="LLM Key"
        name={llmKey.handle}
        id={llmKey.id}
        createdAt={llmKey.createdAt}
        costLabel="Spend (MTD)"
        cost={spend}
      />

      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Total Requests" isLoading={isLoading}>
          <Tooltip
            fluid
            text={
              <div className="grid grid-cols-2 gap-1">
                <div className="font-semibold">Successful</div>
                <div className="text-right">
                  {successfulRequests.toLocaleString()}
                </div>
                <div className="font-semibold">Failed</div>
                <div className="text-right">
                  {failedRequests.toLocaleString()}
                </div>
              </div>
            }
          >
            <span className="border-b border-dotted border-gray-400">
              {totalRequests.toLocaleString()}
            </span>
          </Tooltip>
        </ResourceDetailItem>

        <ResourceDetailItem title="Status" isLoading={isLoading}>
          <Pill variant={llmKey.status === "ACTIVE" ? "success" : "error"}>
            {llmKey.status === "ACTIVE" ? "Active" : "Revoked"}
          </Pill>
        </ResourceDetailItem>
        <ResourceDetailItem title="Note" isLoading={isLoading}>
          {llmKey.note ? (
            llmKey.note
          ) : (
            <span className="text-gray-500 italic">(No description)</span>
          )}
        </ResourceDetailItem>
        <ResourceDetailItem title="Total Tokens" isLoading={isLoading}>
          <Tooltip
            fluid
            text={
              <div className="grid grid-cols-2 gap-1">
                <div className="font-semibold">Input</div>
                <div className="text-right">{inputTokens.toLocaleString()}</div>
                <div className="font-semibold">Output</div>
                <div className="text-right">
                  {outputTokens.toLocaleString()}
                </div>
              </div>
            }
          >
            <span className="border-b border-dotted border-gray-400">
              {totalTokens.toLocaleString()}
            </span>
          </Tooltip>
        </ResourceDetailItem>

        <ResourceDetailItem title="Created By" isLoading={isLoading}>
          <div>
            <div>{llmKey.createdBy.name}</div>
            <div className="text-gray-500 text-sm">
              {llmKey.createdBy.email}
            </div>
          </div>
        </ResourceDetailItem>
      </ResourceDetailsTable>
    </div>
  );
}

function LlmKeyPageHeader() {
  const { id = "" } = useParams();

  const poller = useMemo(() => pollLlmKeyOperations({ id }), [id]);
  const cancel = useMemo(() => cancelLlmKeyOpsPoll(), []);

  usePoller({
    action: poller,
    cancel,
  });

  const loaderLlmKey = useQuery(fetchLlmKeyById({ id }));
  const llmKey = useSelector((s) => selectLlmKeyById(s, { id }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: llmKey.environmentId }),
  );
  const loaderEnv = useQuery(
    fetchEnvironmentById({ id: llmKey.environmentId }),
  );
  const loader = findLoaderComposite([loaderLlmKey, loaderEnv]);

  const crumbs = [
    {
      name: environment.handle,
      to: environmentLlmKeysUrl(environment.id),
      label: "Environment",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    },
  ];

  const tabs: TabItem[] = [
    { name: "Request Logs", href: llmKeyRequestsUrl(id) },
    { name: "Models", href: llmKeyModelsUrl(id) },
  ];

  return (
    <>
      <DetailPageHeaderView
        {...loader}
        breadcrumbs={crumbs}
        titleLabel="LLM Key"
        title={llmKey.handle}
        detailsBox={
          <LlmKeyHeader llmKey={llmKey} isLoading={loader.isLoading} />
        }
        tabs={tabs}
        lastBreadcrumbTo={llmKeyDetailUrl(llmKey.id)}
      />
    </>
  );
}

export const LlmKeyDetailLayout = () => {
  return (
    <AppSidebarLayout
      header={<LlmKeyPageHeader />}
      mainWrapperDivClassName="overflow-x-hidden"
    >
      <Outlet />
    </AppSidebarLayout>
  );
};
